// ─── Écran INGRÉDIENTS : référentiel — base globale + calque personnel ──
// La base (ingredientsBase, items ET rayons) est partagée par TOUS les
// comptes de la suite ; on ne peut jamais la modifier ni la supprimer
// directement — « supprimer » MASQUE, « modifier » CORRIGE, toujours pour
// soi seul (cf. ⚙️ « Gérer mes ingrédients »). Les rayons sont swipables
// (éditer/supprimer) comme les ingrédients ; une catégorie de la base ne se
// supprime que si elle est vide (sinon proposée en masquage refusé — on ne
// laisse pas des ingrédients sans rayon affiché).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import { PageHeader, EmptyState, LigneSwipe, HIT, useUI, useT, useAuth, useEspace, confirmerSuppression } from '@mop/core';
import { ecouterBase } from './services/ingredientsBase';
import {
  ecouterPerso, ajouterPersonnel, modifierAjout, retirerAjout, masquerBase, demasquerBase, modifierBase, annulerModifBase,
  ajouterRayon, modifierRayonAjoute, retirerRayonAjoute, masquerRayonBase, demasquerRayonBase, modifierRayonBase, annulerModifRayonBase,
  ingredientsEffectifs, rayonsEffectifs,
} from './services/ingredientsPerso';
import { rayonDe, ordreRayon, RAYONS } from './services/catalogueIngredients';
import { ecouterListeCourses, enregistrerListeCourses, fusionnerDansListe } from './services/listeCourses';
import FeuilleIngredient from './FeuilleIngredient';
import FicheIngredient from './FicheIngredient';
import FeuilleAjoutCourses from './FeuilleAjoutCourses';
import FeuilleCategorie from './FeuilleCategorie';
import EcranGererIngredients from './EcranGererIngredients';

export default function EcranIngredients() {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const { tenantId, assurerEspace } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [base, setBase] = useState([]);
  const [baseRayons, setBaseRayons] = useState([]);
  const [perso, setPerso] = useState({ ajouts: [], masques: [], modifs: {}, rayonsAjouts: [], rayonsMasques: [], rayonsModifs: {} });
  const [feuille, setFeuille] = useState(null);
  const [fiche, setFiche] = useState(null);       // { item, rayon } — lecture seule
  const [ajout, setAjout] = useState(null);       // ingrédient à mettre aux courses
  const [listeCourses, setListeCourses] = useState([]);
  const [feuilleCategorie, setFeuilleCategorie] = useState(null);
  const [gestion, setGestion] = useState(false);
  const [erreur, setErreur] = useState('');
  // Replié par défaut (ensemble des rayons OUVERTS, vide au départ) — pas
  // un accordéon exclusif, plusieurs rayons peuvent être dépliés en même temps.
  const [ouverts, setOuverts] = useState(() => new Set());
  const basculerRayon = (id) => setOuverts(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  useEffect(() => ecouterBase((items, rayons) => { setErreur(''); setBase(items); setBaseRayons(rayons); },
    (e) => setErreur(`base — ${e?.message || e?.code || 'erreur'}`)), []);
  useEffect(() => ecouterPerso(user?.uid, setPerso, (e) => setErreur(`perso — ${e?.message || e?.code || 'erreur'}`)), [user?.uid]);

  useEffect(() => ecouterListeCourses(tenantId, setListeCourses), [tenantId]);

  const echec = (e) => Alert.alert(t('repas.erreur'), e?.message || '');

  // Ajout aux courses depuis la fiche d'un ingrédient.
  const ajouterIngredients = async (ingredients) => {
    try {
      const espaceId = tenantId || await assurerEspace();
      await enregistrerListeCourses(espaceId, fusionnerDansListe(listeCourses, ingredients));
      Alert.alert(t('courses.ajoutTitre'), t('courses.ajoutMsg', { n: ingredients.length }));
    } catch (e) { echec(e); }
  };

  const items = useMemo(() => ingredientsEffectifs(base, perso), [base, perso]);
  const rayonsEff = useMemo(() => rayonsEffectifs(baseRayons, perso, RAYONS), [baseRayons, perso]);

  const enregistrer = async (f) => {
    try {
      const initial = feuille?.initial;
      if (!initial) await ajouterPersonnel(user.uid, perso, f);
      else if (initial.origine === 'perso') await modifierAjout(user.uid, perso, initial.id, f);
      else await modifierBase(user.uid, perso, initial.id, f);
      setFeuille(null);
    } catch (e) { echec(e); }
  };

  const supprimer = async (item) => {
    try {
      if (item.origine === 'perso') await retirerAjout(user.uid, perso, item.id);
      else await masquerBase(user.uid, perso, item.id);
    } catch (e) { echec(e); }
  };

  const enregistrerCategorie = async (f) => {
    try {
      const initial = feuilleCategorie?.initial;
      if (!initial) await ajouterRayon(user.uid, perso, f);
      else if (initial.origine === 'perso') await modifierRayonAjoute(user.uid, perso, initial.id, f);
      else await modifierRayonBase(user.uid, perso, initial.id, f);
      setFeuilleCategorie(null);
    } catch (e) { echec(e); }
  };

  // Une catégorie de la base ne se supprime (masque) que si elle est VIDE —
  // sinon on refuse (des ingrédients resteraient sans rayon affiché). Une
  // catégorie perso, elle, s'enlève toujours (rien de partagé à préserver).
  const supprimerCategorie = (rayon, nItems) => {
    if (rayon.origine === 'perso') {
      confirmerSuppression(t('ingredients.supprCategorieTitre'), rayon.label,
        () => retirerRayonAjoute(user.uid, perso, rayon.id).catch(echec));
      return;
    }
    if (nItems > 0) {
      Alert.alert(t('ingredients.categorieNonVideTitre'), t('ingredients.categorieNonVideMsg', { n: nItems }));
      return;
    }
    confirmerSuppression(t('ingredients.supprCategorieTitre'), rayon.label,
      () => masquerRayonBase(user.uid, perso, rayon.id).catch(echec));
  };

  // Groupé par catégorie RÉSOLUE (jamais la chaîne brute) : les valeurs
  // inconnues/vides retombent TOUTES sur le même « Autre » — sinon chacune
  // ouvre sa propre section « Autre » en double.
  const groupes = useMemo(() => {
    const map = {};
    for (const i of items) {
      const r = rayonDe(i.categorie, rayonsEff);
      (map[r.id] = map[r.id] || { rayon: r, items: [] }).items.push(i);
    }
    return Object.values(map).sort((a, b) => ordreRayon(a.rayon.id, rayonsEff) - ordreRayon(b.rayon.id, rayonsEff));
  }, [items, rayonsEff]);

  // Emoji d'un ingrédient = celui de son rayon (le catalogue est déjà chargé
  // ici, inutile de passer par le hook partagé).
  const emojiPour = useMemo(() => {
    const parNom = new Map(items.map(i => [String(i.nom || '').trim().toLowerCase(), i.categorie]));
    return (nom) => rayonDe(parNom.get(String(nom || '').trim().toLowerCase()), rayonsEff).emoji || '🛒';
  }, [items, rayonsEff]);

  return (
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      <PageHeader titre={t('ingredients.titre')} iconeTitre="🥕"
        action={{ icone: '＋', label: t('ingredients.nouveau'), onPress: () => setFeuille({ initial: null }) }}>
        <TouchableOpacity onPress={() => setFeuilleCategorie({ initial: null })} hitSlop={HIT} accessibilityLabel={t('ingredients.nouvelleCategorie')}>
          <Text style={s.headerIcone}>🏷️</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setGestion(true)} hitSlop={HIT} accessibilityLabel={t('ingredients.gerer')}>
          <Text style={s.headerIcone}>⚙️</Text>
        </TouchableOpacity>
      </PageHeader>

      <ScrollView contentContainerStyle={s.corps}>
        {!!erreur && (
          <TouchableOpacity style={s.erreur} activeOpacity={0.8} onPress={() => setErreur('')}>
            <Text style={s.erreurTxt}>⚠️ {erreur}</Text>
          </TouchableOpacity>
        )}

        {items.length === 0 ? (
          <EmptyState icon="🥕" texte={t('ingredients.aucun')} aide={t('ingredients.aucunAide')} />
        ) : groupes.map(g => {
          const ferme = !ouverts.has(g.rayon.id);
          return (
            <View key={g.rayon.id} style={s.section}>
              <LigneSwipe espacement={0} enabled
                onEditer={() => setFeuilleCategorie({ initial: g.rayon })}
                onSupprimer={() => supprimerCategorie(g.rayon, g.items.length)}>
                <TouchableOpacity style={s.entete} activeOpacity={0.7} onPress={() => basculerRayon(g.rayon.id)}>
                  <Text style={s.emojiEntete}>{g.rayon.emoji}</Text>
                  <Text style={s.titreEntete}>{g.rayon.label}</Text>
                  {g.rayon.origine === 'perso' && <Text style={s.badge}>{t('ingredients.perso')}</Text>}
                  {g.rayon.modifie && <Text style={[s.badge, s.badgeModifie]}>{t('ingredients.modifieBadge')}</Text>}
                  <Text style={s.chev}>{ferme ? '›' : '▾'}</Text>
                </TouchableOpacity>
              </LigneSwipe>
              {!ferme && (
                <View style={s.corpsSection}>
                  {g.items.map(i => (
                    <LigneSwipe key={i.id} espacement={8} enabled
                      onEditer={() => setFeuille({ initial: i })}
                      onSupprimer={() => supprimer(i)}>
                      {/* Tap = fiche INFO (règle app-wide) ; l'édition passe
                          par le swipe ou par ✏️ dans la fiche. */}
                      <TouchableOpacity style={s.carte} activeOpacity={0.7}
                        onPress={() => setFiche({ item: i, rayon: g.rayon })}>
                        <Text style={s.nom} numberOfLines={1}>{i.nom}</Text>
                        {i.origine === 'perso' && <Text style={s.badge}>{t('ingredients.perso')}</Text>}
                        {i.modifie && <Text style={[s.badge, s.badgeModifie]}>{t('ingredients.modifieBadge')}</Text>}
                        {!!i.unite && <Text style={s.unite}>{i.unite}</Text>}
                      </TouchableOpacity>
                    </LigneSwipe>
                  ))}
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>

      <FicheIngredient visible={!!fiche} ingredient={fiche?.item} rayon={fiche?.rayon}
        onFermer={() => setFiche(null)}
        onEditer={() => { setFeuille({ initial: fiche.item }); setFiche(null); }}
        onSupprimer={() => { supprimer(fiche.item); setFiche(null); }}
        onAjouterListe={() => { setAjout(fiche.item); setFiche(null); }} />

      {/* Ajout aux courses d'un ingrédient SEUL : « pour combien de personnes »
          n'a aucun sens ici, on saisit une quantité dans son unité. Rendue au
          niveau de l'écran (modale sœur de la fiche). */}
      <FeuilleAjoutCourses visible={!!ajout} titre={ajout?.nom} emoji="🛒"
        mode="quantite" ingredientInit={ajout}
        emojiPour={emojiPour}
        onValider={ajouterIngredients} onFermer={() => setAjout(null)} />

      <FeuilleIngredient visible={!!feuille} initial={feuille?.initial} rayons={rayonsEff}
        onEnregistrer={enregistrer} onFermer={() => setFeuille(null)} />

      <FeuilleCategorie visible={!!feuilleCategorie} initial={feuilleCategorie?.initial}
        onEnregistrer={enregistrerCategorie} onFermer={() => setFeuilleCategorie(null)} />

      <EcranGererIngredients visible={gestion} base={base} baseRayons={baseRayons} perso={perso}
        onDemasquer={(id) => demasquerBase(user.uid, perso, id).catch(echec)}
        onRetirerAjout={(id) => retirerAjout(user.uid, perso, id).catch(echec)}
        onAnnulerModif={(id) => annulerModifBase(user.uid, perso, id).catch(echec)}
        onDemasquerRayon={(id) => demasquerRayonBase(user.uid, perso, id).catch(echec)}
        onRetirerRayonAjout={(id) => retirerRayonAjoute(user.uid, perso, id).catch(echec)}
        onAnnulerModifRayon={(id) => annulerModifRayonBase(user.uid, perso, id).catch(echec)}
        onFermer={() => setGestion(false)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  headerIcone: { fontSize: 19, color: UI.accent },
  section: { backgroundColor: UI.card, borderRadius: 13, borderWidth: 1, borderColor: UI.border, marginBottom: 10, overflow: 'hidden' },
  entete: { flexDirection: 'row', alignItems: 'center', gap: 11, padding: 14, overflow: 'hidden' },
  emojiEntete: { fontSize: 20 },
  titreEntete: { flex: 1, fontSize: 15, fontWeight: '700', color: UI.ink },
  chev: { fontSize: 18, color: UI.faint, fontWeight: '300' },
  // Pas de `gap` ici : l'espacement entre lignes vient de LigneSwipe
  // (espacement={8}) — en ajouter un aussi ici double la marge et fait
  // déborder le bouton de suppression du bord de la carte (piège connu).
  corpsSection: { paddingHorizontal: 14, paddingBottom: 6, borderTopWidth: 0.5, borderTopColor: UI.border, paddingTop: 12 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.bg, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, overflow: 'hidden' },
  nom: { flex: 1, fontSize: 14.5, color: UI.ink, fontWeight: '600' },
  badge: { fontSize: 10.5, fontWeight: '800', color: UI.accent, backgroundColor: UI.accentTint, borderRadius: 999, paddingHorizontal: 7, paddingVertical: 2, textTransform: 'uppercase' },
  badgeModifie: { color: UI.muted, backgroundColor: UI.card, borderWidth: 0.5, borderColor: UI.border },
  unite: { fontSize: 12.5, color: UI.muted },
  erreur: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.danger || '#c0392b', padding: 11, marginBottom: 10 },
  erreurTxt: { fontSize: 13, color: UI.danger || '#c0392b', fontWeight: '700' },
});
